import React, { useContext, useEffect,useState } from 'react';
import {
    AlertColor,
    Card,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
} from '@mui/material';
import { Box } from '@mui/system';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import {
    ConsentListData,
    getConsentList,

} from '../../services/configApi/forms/Consent/ConsentServices';
import { useNavigate } from 'react-router';
import { FACESHEET, LEA_REFERRAL } from '../../routes/Routing';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import TitleText from '../../components/formlib/TitleText';
import CustomBackArrow from '../../components/imagepickers/backArrow';
import { ApiError, isCustomError } from '../../services/ApiResponseHandler';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import EmptyScreen from '../../components/shared/EmptyScreen';
import AppPagination from '../../components/shared/AppPagination';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { formatName } from '../../utils/nameUtils';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';
import LeaTableRow from './LeaTableRow';

type Order = 'asc' | 'desc';



interface Data {
    referralDate: string;
    optOut: string;
    actions: string;
}

const headCells: HeadCell<Data>[] = [
    {
        id: 'referralDate',
        labelId: 'ConsentForms.listHeaderReferralDate',
        defaultLabelId: 'Referral Date',
        numeric: false,
        requiredSorting: false,
    },
    {
        id: 'optOut',
        labelId: 'ConsentForms.listHeaderOptOut',
        defaultLabelId: 'Opt Out',
        numeric: false,
        requiredSorting: false,
    },
    {
        id: 'actions',
        labelId: 'ConsentForms.listHeaderActions',
        defaultLabelId: 'Actions',
        numeric: false,
        requiredSorting: false,
    },
];

const LEAReferralConsentDashBoard = () => {
    const [consentList, setConsentList] = useState<ConsentListData[]>([]);
    const [consentEntireList, setConsentEntireList] = useState<ConsentListData[]>(
        [],
    );
    const [consentDataCount, setConsentDataCount] = useState<number>(0);
    const [page, setPage] = useState<number>(0);
    const { toggleLoader } = React.useContext(LoaderContext) as LoaderContextType;
    const [order, setOrder] = useState<Order>();
    const [orderBy, setOrderBy] = useState<keyof Data>();
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);
    const [toastrVariable, setToastrVariable] =
        React.useState<AlertColor>('info');
    const [toastrDefaultMessage, setToastrDefaultMessage] = React.useState('');
    const [toastrId, setToastrId] = React.useState('');
    const [clientName, setClientName] = React.useState('Consent');
    const clientIdentity = localStorage.getItem('ClientId');
       const [APICallSuccessOnZeroCount, setAPICallSuccessOnZeroCount] =
        useState(false);
    const { lookupsDemographics } = useContext(LookUpContext);
    const permissions = useMultiplePermissions([
        'backend.clients',
        'backend.in_active_referrals',
    ]);
    const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
    const hasEditPermission =
        referralStatus === 'Inactive'
            ? permissions['backend.clients'].canEdit &&
            permissions['backend.in_active_referrals'].canEdit
            : permissions['backend.clients'].canEdit ||
            permissions['backend.in_active_referrals'].canEdit;

    const ITEMS_PER_PAGE = 10;


    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data,
    ) => {
        setOrder(order === 'asc' ? 'desc' : 'asc');
        setOrderBy(property);
    };


    function compareValues<T>(a: T, b: T, orderType: Order): number {
        if (orderType === 'asc') {
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
        } else {
            if (a < b) return 1;
            if (a > b) return -1;
            return 0;
        }
    }

    function sortConsents(
        consents: ConsentListData[],
        typeOrder: Order,
        versionOrder: Order,
        dateOrder: Order,
    ): ConsentListData[] {
        return consents.sort((a, b) => {
            const typeComparison = compareValues(
                a.consentType,
                b.consentType,
                typeOrder,
            );
            if (typeComparison !== 0) return typeComparison;

            const versionComparison = compareValues(
                a.version,
                b.version,
                versionOrder,
            );
            if (versionComparison !== 0) return versionComparison;

            return compareValues(
                new Date(a.consentDate).getTime(),
                new Date(b.consentDate).getTime(),
                dateOrder,
            );
        });
    }

    const getConsentListDetails = (clientId: string) => {
        if (clientId) {
            getConsentList('consentDate', 'desc'.toUpperCase(), "lea")
                .then((response) => {
                    const sortedConsents = sortConsents(
                        response.consents,
                        'asc',
                        'desc',
                        'desc',
                    );

                    setConsentEntireList(sortedConsents);
                    const start = page * ITEMS_PER_PAGE;
                    const end = start + ITEMS_PER_PAGE;
                    setConsentList(sortedConsents.slice(start, end));
                    
                    setConsentDataCount(response.totalConsentsCount);
                    if (sortedConsents.slice(start, end).length === 0) {
                        setAPICallSuccessOnZeroCount(true);
                    }
                    toggleLoader(false);
                })
                .catch((error) => {
                    toggleLoader(false);
                    setOpen(true);
                    setToastrVariable('error');
                    if (isCustomError(error)) {
                        const apiError = error as ApiError;
                        setToastrId(apiError.id);
                        setToastrDefaultMessage(apiError.message);
                    } else {
                        setToastrId('ConsentForms.concentLoadError');
                        setToastrDefaultMessage('Failed to load LEA Consent details');
                    }
                });
        }
    };

    useEffect(() => {
        if (lookupsDemographics !== null) {
            const fullName = formatName(
                lookupsDemographics.firstName,
                lookupsDemographics.middleName,
                lookupsDemographics.lastName,
                lookupsDemographics.suffix,
            );
            setClientName(fullName);
        }
        if (clientIdentity) {
            toggleLoader(true);
            getConsentListDetails(clientIdentity);
        }
    }, []);

    useEffect(() => {
        if (clientIdentity) {
            const sortedConsents = sortConsents(
                consentEntireList,
                'asc',
                'desc',
                'desc',
            );
            const start = page * ITEMS_PER_PAGE;
            const end = start + ITEMS_PER_PAGE;
            setConsentList(sortedConsents.slice(start, end));
        }
    }, [page]);

    const onViewClick = (selectedRow: ConsentListData) => {
        navigate(LEA_REFERRAL, { state: { LEA: selectedRow } });
    };


    const handleClose = (
        event?: React.SyntheticEvent | Event,
        reason?: string,
    ) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };
    return (
        <>
            <Box component="main">
                <Box component="section">

                    <SnackBarComponent
                        open={open}
                        handleClose={handleClose}
                        successOrError={toastrVariable}
                        labelId={toastrId}
                        defaultMessageId={toastrDefaultMessage}
                    />
                    <Box component="div" className="rowContainer">
                        <Grid container>
                            <Grid item xs={6} lg={6}>
                                <Box className="flex__ align__items__center">
                                    <Box className="flex__ ">
                                        <CustomBackArrow onClick={() => navigate(FACESHEET)} />
                                    </Box>

                                    <TitleText
                                        className="ml-md"
                                        Sxprops={{
                                            ml: 1,
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            width: '600px',
                                        }}
                                        label={clientName}
                                    />
                                </Box>
                            </Grid>
                            {consentList.length > 0 &&
                                hasEditPermission &&
                                checkPermissionForFeature(
                                    'backend.consents',
                                    'addPermission',
                                ) && (
                                    <Grid item xs={6} lg={6} className="text-align-end">
                                        <ButtonComponent
                                            className="btn-primary btn-submit"
                                            variantType="contained"
                                            type="submit"
                                            labelId="LeaForms.buttonLabel"
                                            defaultLabelId="Add LEA Referral Consent"
                                            onClick={() => navigate(LEA_REFERRAL)}
                                        />
                                    </Grid>
                                )}
                        </Grid>
                    </Box>

                    {consentList.length > 0 && (
                        <Card>
                            <TableContainer component={Paper}>
                                <Table aria-label="simple table">
                                    <TableHeader
                                        className="listDataTableHead"
                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleRequestSort}
                                        headerNames={headCells}
                                        checkBoxRequired={false}
                                    />
                                    <TableBody className="tableRowcss">
                                        {consentList.length !== 0 &&
                                            consentList.map((consentData) => (
                                                <LeaTableRow
                                                    key={consentData.consentId}
                                                    data={consentData}
                                                    onView={onViewClick}
                                                    //offlineFlag={offlineMode}
                                                />
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    )}
                    {consentDataCount > ITEMS_PER_PAGE && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <AppPagination
                                pageNumber={page}
                                paginationCount={consentDataCount}
                                handleChangePage={(_event, newPage) => {
                                    if (newPage - 1 !== page) {
                                        setPage(newPage - 1);
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>


            {APICallSuccessOnZeroCount && consentList.length === 0 && (
                <Box component="main" className="mb-md">
                    <Box component="section">
                        <EmptyScreen
                            titleLabelId="ConsentForms.emptyMessage"
                            defaultTitleText="No Consent Forms added yet"
                            buttonLabelId="LeaForms.buttonLabel"
                            defaultButtonTitleText="Add LEA Consents"
                            showButton={
                                hasEditPermission &&
                                checkPermissionForFeature('backend.consents', 'addPermission')
                            }
                            onButtonClick={() => {
                                navigate(LEA_REFERRAL);
                            }}
                        />
                    </Box>
                </Box>
            )}
        </>
    );
};

export default LEAReferralConsentDashBoard;
